import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Languages, ClipboardList, HeartHandshake, ArrowRight } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1697305593259-d96aa6ed0e14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBzZW5pb3IlMjBjaXRpemVufGVufDB8fHx8MTc4OTYyNzI1M3ww&ixlib=rb-4.1.0&q=85";

export default function CareManagerPage() {
  return (
    <div className="p1-container py-14 lg:py-20" data-testid="care-manager-page">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <div>
          <p className="p1-eyebrow">The heart of ParentOne</p>
          <h1 className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight text-stone-900 sm:text-4xl lg:text-5xl">
            Your dedicated <span className="text-emerald-900">Care Manager</span>
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-stone-600">
            Every ParentOne family gets one dedicated Care Manager — a background-verified local professional who is your single point of contact. They visit your parents, coordinate doctors and services, and keep you informed after every interaction.
          </p>
          <Link to="/onboarding" className="p1-btn-primary mt-8" data-testid="cm-page-cta">Meet your Care Manager <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="overflow-hidden rounded-3xl border border-stone-200 shadow-lg">
          <img src={IMG} alt="Care manager assisting a senior with paperwork" className="h-[400px] w-full object-cover" data-testid="cm-page-image" />
        </div>
      </div>

      <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { icon: ShieldCheck, title: "Verified & trained", text: "Identity checks, background verification and structured training before meeting any family." },
          { icon: Languages, title: "Speaks their language", text: "Hindi, Marwari and English — matched to your parents' comfort." },
          { icon: ClipboardList, title: "Accountable on record", text: "Every visit is checked in, documented and reported to the family." },
          { icon: HeartHandshake, title: "Continuity of care", text: "The same person, visit after visit — someone your parents genuinely know and trust." },
        ].map((c, i) => (
          <div key={c.title} className="p1-card p-6" data-testid={`cm-feature-${i}`}>
            <c.icon className="h-7 w-7 text-emerald-800" />
            <h3 className="mt-3 font-heading text-lg font-semibold text-stone-900">{c.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{c.text}</p>
          </div>
        ))}
      </div>

      <div className="mt-16 rounded-3xl border border-stone-200 bg-white p-8 sm:p-10">
        <h2 className="font-heading text-2xl font-bold text-stone-900">What a Care Manager actually does</h2>
        <div className="mt-6 grid gap-4 text-sm text-stone-700 sm:grid-cols-2">
          {["Scheduled wellbeing visits with written family reports", "Accompanies parents to doctors and hospitals", "Coordinates nurses, attendants, physiotherapy and diagnostics", "Manages medication reminders and pharmacy refills", "Spots concerns early and escalates to family and operations", "Arranges errands, travel and everyday assistance", "First local responder in the emergency workflow", "Keeps the Parent Care File accurate and up to date"].map((t) => (
            <p key={t} className="flex items-start gap-2"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-terracotta" />{t}</p>
          ))}
        </div>
      </div>
    </div>
  );
}
